import React, { useMemo, useState } from 'react';
import { useAuth } from '../../commons/Auth';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import _ from 'lodash';
import moment from 'moment';
import {
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import DateInput from '../../commons/DateInput';
import {
  BtnModalTable,
  BtnOutlineGreen,
  BtnOutlineRed,
} from '../../styled-components';
import axios from 'axios';
import { useLoading } from '../../commons/Loading';

function ManageCondoButton({ condo = {}, onConfirm, profile = {} }) {
  const [t] = useTranslation('CONDOS');
  const [{ newIsAdmin, newToken } = {}] = useAuth();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  const [modal, setModal] = useState(false);
  const [maxStartDate, setMaxStartDate] = useState(null);
  const [minEndDate, setMinEndDate] = useState(null);

  const INITIAL_CONDO_STATE = useMemo(
    () => ({
      admins: condo.admins,
      name: condo.name,
      vatCode: condo.vatCode,
      street: condo.street,
      postalCode: condo.postalCode,
      city: condo.city,
      province: condo.province,
      manufacturedUnitNumber: condo.manufacturedUnitNumber,
      studioNumber: condo.studioNumber,
      adminCollab: condo.adminCollab || { _id: '', name: '' },
      certifier: condo.certifier || { _id: '', businessName: '' },
      balanceId: condo.balance?._id || '',
      balanceStartDate: condo.balance?.startDate || '',
      balanceEndDate: condo.balance?.endDate || '',
      startDate: condo.currentAdmin?.startDate || '',
      endDate: condo.currentAdmin?.endDate || '',
    }),
    [condo]
  );

  const [formValues, setFormValues] = useState({
    ...INITIAL_CONDO_STATE,
    admin: {
      _id: profile._id,
      businessName: profile.businessName,
    },
  });

  const [formErrors, setFormErrors] = useState({
    endDateTooSmall: false,
    endDateTooBig: false,
    endDateTooSmallBalance: false,
    endDateTooCloseToNextAdmin: false,
    startDateTooBig: false,
    startDateTooSmallBalance: false,
    startDateExcedeMaxStartDate: false,
    endDateExcedeMinEndDate: false,
  });

  const handleChangeDate = async () => {
    const {
      admins,
      balanceStartDate,
      balanceEndDate,
      startDate,
      endDate,
    } = formValues;

    const errors = {};

    if (
      moment(endDate).startOf('day').isBefore(moment(startDate).startOf('day'))
    )
      errors.endDateTooSmall = true;

    if (
      moment(endDate)
        .startOf('day')
        .isAfter(moment(balanceEndDate).startOf('day'))
    )
      errors.endDateTooBig = true;

    if (
      moment(endDate)
        .startOf('day')
        .isBefore(moment(balanceStartDate).startOf('day'))
    )
      errors.endDateTooSmallBalance = true;

    if (
      !moment(condo.currentAdmin?.startDate)
        .startOf('day')
        .isBefore(moment(balanceStartDate).startOf('day'))
    ) {
      if (
        moment(startDate).startOf('day').isAfter(moment(endDate).startOf('day'))
      )
        errors.startDateTooBig = true;

      if (
        moment(startDate)
          .startOf('day')
          .isBefore(moment(balanceStartDate).startOf('day'))
      )
        errors.startDateTooSmallBalance = true;

      const currentAdmin = admins.find((a) => a.id === condo.currentAdmin?._id);

      if (currentAdmin && admins.length > 1) {
        const sortedAdmins = admins
          .filter((a) => a.id !== currentAdmin.id)
          .sort((a, b) => moment(a.startDate).diff(moment(b.startDate)));

        const nextAdmin = sortedAdmins.find((a) =>
          moment(a.startDate)
            .startOf('day')
            .isAfter(moment(currentAdmin.startDate).startOf('day'))
        );

        if (nextAdmin) {
          const maxAllowedEndDate = moment(nextAdmin.startDate)
            .subtract(1, 'day')
            .startOf('day');
          const selectedEndDate = moment(endDate).startOf('day');

          if (selectedEndDate.isAfter(maxAllowedEndDate)) {
            errors.endDateTooCloseToNextAdmin = true;
          }
        }
      }

      if (
        maxStartDate &&
        moment(startDate)
          .startOf('day')
          .isAfter(moment(maxStartDate).startOf('day'))
      ) {
        errors.startDateExcedeMaxStartDate = true;
      }
    }

    if (
      minEndDate &&
      moment(endDate).startOf('day').isBefore(moment(minEndDate).startOf('day'))
    ) {
      errors.endDateExcedeMinEndDate = true;
    }

    if (Object.keys(errors).length) {
      return setFormErrors(errors);
    }

    try {
      let admins = formValues.admins.map((elem) => ({
        id: elem.id,
        startDate: elem.startDate,
        endDate: elem.endDate,
      }));
      admins = admins
        .filter((elem) => elem.id === condo.currentAdmin?._id)
        .map((elem) => {
          return {
            id: elem.id,
            startDate: startDate,
            endDate: endDate,
          };
        });
      onConfirm(
        {
          ..._.omit(formValues, [
            'currentAdmin',
            'admin',
            'startDate',
            'endDate',
          ]),
          admins,
        },
        condo._id
      );
      toggle();
    } catch (error) {
      alert.error(error.message);
    }
  };

  const searchExtraInvoiceMaxMinDate = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/condos/extra-invoice-date/${condo._id}`,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data: dataResponse = [] } = response;

    if (dataResponse.maxStartDate) {
      setMaxStartDate(
        moment(dataResponse.maxStartDate).startOf('day').toDate()
      );
    }
    if (dataResponse.minEndDate) {
      setMinEndDate(moment(dataResponse.minEndDate).startOf('day').toDate());
    }
  };

  const toggle = () => {
    setFormErrors({
      endDateTooSmall: false,
      endDateTooBig: false,
      endDateTooSmallBalance: false,
      endDateTooCloseToNextAdmin: false,
      startDateTooBig: false,
      startDateTooSmallBalance: false,
      startDateExcedeMaxDate: false,
      endDateExcedeMinDate: false,
    });
    if (!modal) {
      searchExtraInvoiceMaxMinDate();
    }
    setModal(!modal);
  };

  const { startDate, endDate, balanceStartDate } = formValues;

  return (
    <React.Fragment>
      <BtnModalTable
        type="button"
        className="mx-2 px-2"
        onClick={(event) => {
          event.stopPropagation();
          toggle();
        }}
      >
        Gestione amministrazione
      </BtnModalTable>

      <Modal isOpen={modal} toggle={toggle} className="new-condo-modal">
        <ModalHeader toggle={toggle}>{t('CHANGE_REPORT_DATE')}</ModalHeader>

        <ModalBody>
          <Row>
            <Col xs={10}>
              <Form>
                <FormGroup>
                  <Label for="admin">{t('CURRENT_ADMIN')}</Label>
                  <Input
                    type="text"
                    name="admin"
                    value={condo.currentAdmin?.name}
                    disabled
                  />
                </FormGroup>
              </Form>
            </Col>
          </Row>
          <Form>
            <Row>
              <Col sm="12">
                <p className="text-bold mb-2">{t('BALANCE_PERIOD')}:</p>
              </Col>
              <Col xs="12" md="6" lg="6">
                <FormGroup>
                  <Label>{t('BALANCE_START_DATE')}</Label>
                  <DateInput
                    name="balanceStartDate"
                    value={condo.balance?.startDate}
                    disabled={true}
                  />
                </FormGroup>
              </Col>
              <Col xs="12" md="6" lg="6">
                <FormGroup>
                  <Label>{t('BALANCE_END_DATE')}</Label>
                  <DateInput
                    name="balanceEndDate"
                    value={condo.balance?.endDate}
                    disabled={true}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
          <Row>
            <Col sm="12">
              <p className="text-bold mb-2">{t('MANAGEMENT_PERIOD')}:</p>
            </Col>
            <Col sm="6">
              <Form>
                <FormGroup>
                  <Label for="startDate">{t('START_DATE')}</Label>
                  <DateInput
                    name="startDate"
                    value={startDate}
                    disabled={moment(condo.currentAdmin?.startDate)
                      .startOf('day')
                      .isBefore(moment(balanceStartDate).startOf('day'))}
                    onChange={(startDate) => {
                      setFormValues((state) => ({
                        ...state,
                        startDate: moment(startDate).startOf('day'),
                      }));
                    }}
                  />
                  {moment(condo.currentAdmin?.startDate)
                    .startOf('day')
                    .isBefore(moment(balanceStartDate).startOf('day')) && (
                    <FormText className="error-message">
                      {t('START_DATE_NOT_IN_BALANCE_ERROR')}
                    </FormText>
                  )}
                  {formErrors.startDateTooBig && (
                    <FormText className="error-message">
                      {t('START_DATE_TOO_BIG_ERROR')}
                    </FormText>
                  )}
                  {formErrors.startDateTooSmallBalance && (
                    <FormText className="error-message">
                      {t('START_DATE_ERROR')}
                    </FormText>
                  )}
                  {formErrors.startDateExcedeMaxStartDate && (
                    <FormText className="error-message">
                      {t('START_DATE_EXCEDE_MAX_DATE_ERROR')}
                    </FormText>
                  )}
                </FormGroup>
              </Form>
            </Col>

            <Col sm="6">
              <Form>
                <FormGroup>
                  <Label for="endDate">{t('END_DATE')}</Label>
                  <DateInput
                    name="endDate"
                    value={endDate}
                    onChange={(endDate) => {
                      setFormValues((state) => ({
                        ...state,
                        endDate: moment(endDate).startOf('day'),
                      }));
                    }}
                  />
                  {formErrors.endDateTooBig && (
                    <FormText className="error-message">
                      {t('END_DATE_TOO_BIG_ERROR')}
                    </FormText>
                  )}
                  {formErrors.endDateTooSmall && (
                    <FormText className="error-message">
                      {t('END_DATE_TOO_SMALL_ERROR')}
                    </FormText>
                  )}
                  {formErrors.endDateTooSmallBalance && (
                    <FormText className="error-message">
                      {t('END_DATE_TOO_SMALL_BALANCE_ERROR')}
                    </FormText>
                  )}
                  {formErrors.endDateTooCloseToNextAdmin && (
                    <FormText className="error-message">
                      {t('END_DATE_TOO_CLOSE_TO_NEXT_ADMIN_ERROR')}
                    </FormText>
                  )}
                  {formErrors.endDateExcedeMinEndDate && (
                    <FormText className="error-message">
                      {t('END_DATE_EXCEDE_MIN_DATE_ERROR')}
                    </FormText>
                  )}
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <BtnOutlineRed type="button" className="uppercase" onClick={toggle}>
            {t('COMMON:CANCEL')}
          </BtnOutlineRed>
          <BtnOutlineGreen
            className="uppercase"
            onClick={() => {
              handleChangeDate();
            }}
          >
            {t('COMMON:CONFIRM')}
          </BtnOutlineGreen>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
}

export default ManageCondoButton;
